/**
 * swingDetect — pivot-point swing high/low detection.
 *
 * A swing high is a bar whose HIGH is strictly greater than the high of
 * the N bars to its left AND the N bars to its right. Swing low is the
 * mirror on LOW. The N value (default 3) controls how "obvious" a pivot
 * must be — higher N = fewer, more significant swings; lower N = more
 * but noisier swings.
 *
 * Used by Wave C pattern detectors:
 *   - Uno Reverse (Rake) — entry on retest of a broken swing
 *   - Turtle Soup (Raschke) — failed break of a recent swing extreme
 *   - H2/L2 (Brooks) — counting pullback legs since last swing
 *
 * Returns swings in chronological order. Each swing carries its bar
 * index so callers can correlate with other parallel-array data
 * (EMAs, ATR, etc.).
 *
 * Pure function. No side effects. Two-pass scan, O(n).
 */

export const DEFAULT_PIVOT_BARS = 3;

/**
 * Detect swings in a bars array.
 *
 * @param {Array<{ time:number, high:number, low:number }>} bars
 *        Sorted ascending. Must have time + high + low fields.
 * @param {Object} [options]
 * @param {number} [options.leftBars=3]   Bars to left required for pivot.
 * @param {number} [options.rightBars=3]  Bars to right required for pivot.
 * @returns {Array<{
 *   type: 'high' | 'low',
 *   index: number,    // position in `bars`
 *   time: number,     // bars[index].time
 *   price: number,    // the pivot price (high or low)
 * }>}
 *   In chronological order. Empty array when bars too few or input invalid.
 *
 * NOTE: bars within `rightBars` of the END of the array can't be evaluated
 * yet (their right neighbors don't exist) and are excluded. This is by
 * design — a swing high isn't confirmed until enough later bars exist
 * to verify nothing exceeded it.
 */
export const detectSwings = (bars, options = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  const left = Math.floor(Number(options.leftBars ?? DEFAULT_PIVOT_BARS));
  const right = Math.floor(Number(options.rightBars ?? DEFAULT_PIVOT_BARS));
  if (![left, right].every(n => Number.isFinite(n) && n >= 1)) return [];
  if (bars.length < left + right + 1) return [];

  const swings = [];
  for (let i = left; i < bars.length - right; i++) {
    const cur = bars[i];
    const hi = Number(cur?.high);
    const lo = Number(cur?.low);
    if (!Number.isFinite(hi) || !Number.isFinite(lo)) continue;

    // Swing high check
    let isSwingHigh = true;
    for (let j = i - left; j <= i + right; j++) {
      if (j === i) continue;
      const otherHi = Number(bars[j]?.high);
      if (!Number.isFinite(otherHi) || otherHi >= hi) { isSwingHigh = false; break; }
    }
    if (isSwingHigh) {
      swings.push({ type: 'high', index: i, time: Number(cur.time), price: hi });
      continue;
    }

    // Swing low check
    let isSwingLow = true;
    for (let j = i - left; j <= i + right; j++) {
      if (j === i) continue;
      const otherLo = Number(bars[j]?.low);
      if (!Number.isFinite(otherLo) || otherLo <= lo) { isSwingLow = false; break; }
    }
    if (isSwingLow) {
      swings.push({ type: 'low', index: i, time: Number(cur.time), price: lo });
    }
  }

  return swings;
};

/**
 * Filter to the most recent N swings of a specific type, ordered
 * newest-first. Convenience for "find the last broken support" style
 * lookups.
 */
export const recentSwings = (swings, type, n = 5) => {
  if (!Array.isArray(swings)) return [];
  const filtered = swings.filter(s => s?.type === type);
  filtered.reverse();
  return filtered.slice(0, n);
};

/**
 * Find the most recent swing of either type whose index is < `beforeIdx`.
 * Returns null when none exist.
 */
export const mostRecentSwingBefore = (swings, beforeIdx) => {
  if (!Array.isArray(swings) || swings.length === 0) return null;
  for (let i = swings.length - 1; i >= 0; i--) {
    if (swings[i].index < beforeIdx) return swings[i];
  }
  return null;
};
